import {
  Alert, Button, Container, Fieldset, Grid, Group, LoadingOverlay,
  Select, Stack, Text, Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconEdit, IconRotate, IconX } from '@tabler/icons-react';
import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDirtyFormGuard, useFormSubmitSaver } from '../hooks/useDirtyFormGuard';
import { StickyDetailHeader } from '../components/StickyHeader';
import { useAlertModal } from '../components/AlertModalProvider';

import { ApiError } from '../api/client';
import { applyApiErrorToForm, isFieldLevelApiError } from '../utils/applyApiError';
import type { AccountPartyCreateInput, AccountPartyRole, AccountPartyUpdateInput } from '../api/types';
import { AccountPicker, PersonPicker } from '../components/EntityPicker';
import {
  useAccountParty, useCreateAccountParty,
  useRestoreAccountParty, useUpdateAccountParty,
} from '../hooks/useAccountParties';

const ROLE_OPTS = [
  { value: 'JOINT_HOLDER', label: 'Joint holder' },
  { value: 'INSURANCE_BENEFICIARY', label: 'Insurance beneficiary' },
];

interface FormValues {
  account_id: string;
  person_id: string;
  role: AccountPartyRole;
  notes: string;
}

const EMPTY: FormValues = { account_id: '', person_id: '', role: 'JOINT_HOLDER', notes: '' };

export function AccountPartyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const presetAccountId = isNew ? (searchParams.get('account_id') ?? '') : '';
  // D2-polish 2: pre-fill the person picker after returning from a
  // [+ Create new person] flow.
  const presetPersonId = isNew ? (searchParams.get('person_id') ?? '') : '';

  const { data, isLoading } = useAccountParty(isNew ? undefined : id);
  const create = useCreateAccountParty();
  const update = useUpdateAccountParty(id ?? '');
  const restore = useRestoreAccountParty();

  const initial: FormValues = isNew || !data ? {
    ...EMPTY, account_id: presetAccountId, person_id: presetPersonId,
  } : {
    account_id: data.account_id, person_id: data.person_id, role: data.role,
    notes: data.notes ?? '',
  };

  const form = useForm<FormValues>({
    initialValues: initial,
    validate: {
      account_id: (v) => (v.length >= 32 ? null : 'Account required'),
      person_id:  (v) => (v.length >= 32 ? null : 'Person required'),
    },
  });
  // Pedro 2026-10-10: leaving with unsaved edits offers "Save & leave" (runs this page's Save).
  const saveForGuard = useFormSubmitSaver('account-party-form', form.isDirty(), !isNew);
  const confirmDiscard = useDirtyFormGuard(form.isDirty(), saveForGuard);
  const [editMode, setEditMode] = React.useState(isNew);
  const { showAlert } = useAlertModal();

  if (!isNew && data && form.values.account_id === '' && data.account_id !== '') {
    form.setValues(initial); form.resetDirty(initial);
  }

  const _err = isNew ? create.error : update.error;
  const apiError = (_err instanceof ApiError && !isFieldLevelApiError(_err))
    ? _err.detail : null;
  const isDeleted = !!data?.deleted_at;

  function handleSubmit(values: FormValues) {
    if (isNew) {
      const payload: AccountPartyCreateInput = {
        account_id: values.account_id,
        person_id: values.person_id,
        role: values.role,
      };
      if (values.notes) payload.notes = values.notes;
      create.mutate(payload, {
        onSuccess: (r) => {
          form.resetDirty();
          // Bug fix (2026-06-09 during A1.f): see AgreementDetailPage
          // create.mutate onSuccess for the rationale. Flip editMode
          // explicitly so the fallback navigate (`/account-parties/${id}`)
          // lands in read mode.
          setEditMode(false);
          if (r.pending) {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: 'Your new account-party was filed. It is not yet visible — Compliance must approve it first.',
            });
            navigate(`/change-requests/${r.changeRequestId}`);
          } else {
            notifications.show({ message: 'Account-party created', color: 'green' });
            const returnTo = searchParams.get('return_to');
            if (returnTo) {
              navigate(returnTo, { replace: true });
            } else {
              navigate(`/account-parties/${r.entity.id}`, { replace: true });
            }
          }
        },
        onError: (err) => { applyApiErrorToForm(form, err); },
      });
    } else {
      const payload: AccountPartyUpdateInput = {};
      if (values.notes) payload.notes = values.notes;
      update.mutate(payload, {
        onSuccess: (r) => {
          form.resetDirty(values);
          setEditMode(false);
          if (r.pending) {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: 'Your edits were filed. The live record is unchanged until Compliance approves.',
            });
          } else {
            notifications.show({ message: 'Saved', color: 'green' });
          }
        },
        onError: (err) => { applyApiErrorToForm(form, err); },
      });
    }
  }

  return (
    <Container size="xl">
      <Stack>
        <StickyDetailHeader
          editing={editMode}
          title={isNew ? 'New account-party' : data?.role ?? '…'}
          backOnClick={() => confirmDiscard(() => {
            // #391 sweep (2026-06-09): clear dirty state BEFORE navigating
            // so the passive useBlocker (DirtyFormProvider, #258) doesn't
            // re-trigger the discard modal on the navigate.
            form.setValues(initial);
            form.resetDirty(initial);
            setEditMode(false);
            navigate(-1);
          })}
          primary={
            !editMode ? (
              <Button leftSection={<IconEdit size={16} />} onClick={() => setEditMode(true)}>
                Edit
              </Button>
            ) : (
              <Group gap="xs">
                <Button variant="default" leftSection={<IconX size={16} />}
                  onClick={() => {
                    confirmDiscard(() => {
                      form.setValues(initial);
                      form.resetDirty(initial);
                      setEditMode(false);
                    });
                  }}>
                  Cancel
                </Button>
                <Button form="account-party-form" type="submit"
                  loading={create.isPending || update.isPending}
                  disabled={!form.isDirty() && !isNew}>
                  {isNew ? 'Save' : 'Save changes'}
                </Button>
              </Group>
            )
          }
          destructive={
            /* #8 (2026-06-16, Pedro): role-assignment page edits the role
               only — no Delete; break the link from the list. Restore stays
               for a soft-deleted assignment. */
            !isNew && data && isDeleted ? (
              <Button variant="light" color="green" leftSection={<IconRotate size={16} />}
                onClick={() => restore.mutate(data.id)} loading={restore.isPending}>Restore</Button>
            ) : null
          }
        />

        {apiError && <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>{apiError}</Alert>}

        <form id="account-party-form" onSubmit={form.onSubmit(handleSubmit)}>
          <Stack pos="relative">
            <LoadingOverlay visible={isLoading} />
            <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
              <Fieldset legend="Linkage (immutable after creation)">
                <Grid>
                  <Grid.Col span={6}>
                    <AccountPicker
                      label="Account" required
                      disabled={!isNew || !!presetAccountId}
                      value={form.values.account_id}
                      onChange={(v) => form.setFieldValue('account_id', v ?? '')}
                    />
                    {isNew && presetAccountId && (
                      <Text size="xs" c="dimmed" mt={2}>Locked — adding from this account</Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <PersonPicker
                      label="Person" required
                      disabled={!isNew || !!presetPersonId}
                      value={form.values.person_id}
                      onChange={(v) => form.setFieldValue('person_id', v ?? '')}
                      onCreateNew={isNew && !presetPersonId ? () => {
                        const here = window.location.pathname + window.location.search;
                        const target = `/persons/new?return_to=${encodeURIComponent(here)}&return_field=person_id`;
                        navigate(target);
                      } : undefined}
                    />
                    {isNew && presetPersonId && (
                      <Text size="xs" c="dimmed" mt={2}>Locked — newly created person</Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Select label="Role" data={ROLE_OPTS} {...form.getInputProps('role')} disabled={!isNew} allowDeselect={false} />
                  </Grid.Col>
                </Grid>
              </Fieldset>
              <Textarea label="Notes" autosize minRows={2} {...form.getInputProps('notes')} />
            </fieldset>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}
